import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, Subscription } from 'rxjs';
import {
  UserPermissionEntry,
  UserpermissionService,
} from '../../../services/userpermission.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { globalUser } from '../../../helpers/globalFields';
import { MasterDataService } from '../../../services/master-data.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { InviteUserComponent } from '../invite-user/invite-user.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

/**
 * Internal row model used by the permission grid.
 * Tracks both the original state (from the API) and the draft state (from checkbox interaction)
 * so the update function can compute diffs and only call create/delete where needed.
 */
export interface PermissionRow {
  usr_id: number;
  usr_name: string;
  usr_mail: string;

  /**
   * Map of permission key → { checked: boolean, entryId: number | null }
   * entryId is the PK of the userpermission row if it exists, null if the user doesn't have it.
   */
  original: Record<string, { checked: boolean; entryId: number | null }>;
  draft: Record<string, boolean>;
}

/**
 * Admin page component for managing user permissions within the admin's organization.
 *
 * Displays a grid where rows = users, columns = permissions.
 * Each cell is a checkbox. On "Update", the component diffs the draft state against
 * the original and fires the minimum set of create/delete API calls required.
 *
 * Permission columns are loaded dynamically from the master permissions API so that
 * adding a new permission to the database automatically appears as a new column
 * with no frontend changes required.
 *
 * @author julian.f.verhey
 */
@Component({
  selector: 'app-panel',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    MatCheckboxModule,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormField,
    MatLabel,
    MatInputModule,
  ],
  templateUrl: './panel.component.html',
  styleUrl: './panel.component.scss',
})
export class PanelComponent implements OnInit, OnDestroy {
  rows: PermissionRow[] = [];
  orgName = '';
  currentAppLogo = environment.appLogoPath;
  loading = false;
  saving = false;
  searchQuery = '';
  /** Populated from the master permissions API — drives the grid columns. */
  permissions: { id: number; key: string; label: string; category: string }[] =
    [];

  constructor(
    private permissionService: UserpermissionService,
    private snackBar: MatSnackBar,
    private masterDataService: MasterDataService,
    private dialog: MatDialog,
    private router: Router,
  ) {}
  private userSub?: Subscription;
  /**
   * Bootstrap sequence:
   *   1. Fetch master permissions to build column definitions.
   *   2. Once permissions are loaded, fetch user data to build rows.
   *   3. Subscribe to user stream so the grid reloads if the active user changes
   *      (e.g. organization switch). Permissions are re-fetched on each reload
   *      to stay in sync.
   */
  ngOnInit(): void {
    this.loadPermissionsThenData();
    this.userSub = globalUser.getUserStream().subscribe((user) => {
      if (user) {
        this.loadPermissionsThenData();
      }
    });

    this.userSub = globalUser.getUserStream().subscribe((user) => {
      if (user) {
        const perms: string[] = user.permissions ?? [];

        // Redirect if organization.admin was revoked
        if (
          !perms.includes('organization.admin') &&
          !perms.includes('system.admin')
        ) {
          this.router.navigate(['/ticket/list']);
          return;
        }

        this.loadPermissionsThenData();
      }
    });
  }

  /**
   * Fetch master permissions from the API, map them to column definitions,
   * then trigger the user-permission grid data load.
   *
   * Permissions must be loaded before row data so that buildRow() can correctly
   * map each UserPermissionEntry against the full permission key set.
   */
  loadPermissionsThenData(): void {
    this.masterDataService.getMasterPermissionList().subscribe({
      next: (perms) => {
        this.permissions = perms.map((p) => ({
          id: p.id,
          key: p.mpm_type,
          label: p.mpm_type.replace('.', ' '),
          category: p.mpm_type.split('.')[0],
        }));
        const order = ['ticket', 'report', 'user'];
        this.permissions.sort((a, b) => {
          const ai = order.indexOf(a.category);
          const bi = order.indexOf(b.category);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
        this.loadData();
      },
      error: () => {
        this.snackBar.open('Failed to load permission definitions.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  /**
   * Fetch all users and their permission assignments for the admin's org context
   * and build the internal grid row models.
   */
  loadData(): void {
    this.loading = true;
    this.permissionService.getForOrganization().subscribe({
      next: (res) => {
        this.orgName = res.data[0]?.org_name ?? '';
        this.rows = res.data.map((entry) => this.buildRow(entry));
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open('Failed to load user permissions.', 'Close', {
          duration: 4000,
        });
        this.loading = false;
        console.error(err);
      },
    });
  }

  /**
   * Delete a user and revoke all their permissions within this organization.
   * Asks for confirmation before proceeding.
   *
   * @param row - The PermissionRow of the user to delete
   */
  onDeleteUser(row: PermissionRow): void {
    // panel.component.ts — update the confirm text
    const confirmed = window.confirm(
      `Remove ${row.usr_name} (${row.usr_mail}) from ${this.orgName}?\n\nThis will revoke all their permissions for this organization. Their account will not be deleted.`,
    );
    if (!confirmed) return;

    this.permissionService.deleteUserFromOrganization(row.usr_id).subscribe({
      next: () => {
        this.snackBar.open(`${row.usr_name} has been removed.`, 'Close', {
          duration: 3000,
        });
        this.loadPermissionsThenData();
      },
      error: (err) => {
        this.snackBar.open(
          'Failed to remove user. Please try again.',
          'Close',
          { duration: 4000 },
        );
        console.error(err);
      },
    });
  }

  /**
   * Build an internal PermissionRow from an API UserPermissionEntry.
   * Populates both original (source of truth) and draft (editable copy) state
   * for every permission in the current column definitions.
   *
   * NOTE: entryId is stubbed as -1 for existing permissions until the API is
   * extended to return per-permission row PKs. Delete calls are guarded against
   * this stub value in submitChanges().
   *
   * @param entry - A single user-permission entry from the API
   * @returns A PermissionRow ready for the grid
   */
  private buildRow(entry: UserPermissionEntry): PermissionRow {
    const original: PermissionRow['original'] = {};
    const draft: PermissionRow['draft'] = {};

    for (const perm of this.permissions) {
      const ref = (entry.permissions as any[]).find((p) =>
        typeof p === 'string' ? p === perm.key : p.key === perm.key,
      );
      const entryId = ref && typeof ref === 'object' ? ref.entry_id : null;
      original[perm.key] = { checked: !!ref, entryId };
      draft[perm.key] = !!ref;
    }

    return {
      usr_id: entry.usr_id,
      usr_name: entry.usr_name,
      usr_mail: entry.usr_mail,
      original,
      draft,
    };
  }

  /**
   * Returns true if the draft state differs from the original for any permission
   * on any row. Used to enable/disable the Update button.
   */
  get hasPendingChanges(): boolean {
    return this.rows.some((row) =>
      this.permissions.some(
        (perm) => row.draft[perm.key] !== row.original[perm.key].checked,
      ),
    );
  }

  /**
   * Diff the draft against the original and submit only the changed assignments
   * via create (newly checked) or delete (newly unchecked) API calls.
   * All calls are fired in parallel with forkJoin.
   * On completion the grid reloads to sync clean state and real entry IDs.
   */
  submitChanges(): void {
    const creates$: Observable<any>[] = [];
    const deletes$: Observable<any>[] = [];
    const affectedUserIds = new Set<number>(); // track which users are affected

    for (const row of this.rows) {
      for (const perm of this.permissions) {
        const wasChecked = row.original[perm.key].checked;
        const isChecked = row.draft[perm.key];
        const entryId = row.original[perm.key].entryId;

        if (!wasChecked && isChecked) {
          creates$.push(
            this.permissionService.createPermission(row.usr_id, perm.id),
          );
          affectedUserIds.add(row.usr_id);
        } else if (wasChecked && !isChecked && entryId !== null) {
          deletes$.push(this.permissionService.deletePermission(entryId));
          affectedUserIds.add(row.usr_id);
        }
      }
    }

    const all$ = [...creates$, ...deletes$];
    if (!all$.length) return;

    this.saving = true;
    forkJoin(all$).subscribe({
      next: () => {
        // Notify each affected user exactly once after all changes are complete
        affectedUserIds.forEach((userId) => {
          this.permissionService.notifyUser(userId).subscribe();
        });

        this.snackBar.open('Permissions updated successfully.', 'Close', {
          duration: 3000,
        });
        this.saving = false;
        this.loadPermissionsThenData();
      },
      error: (err) => {
        this.snackBar.open(
          'One or more updates failed. Please try again.',
          'Close',
          { duration: 5000 },
        );
        this.saving = false;
        console.error(err);
      },
    });
  }

  /** Rows filtered by the search query against name and mail. */
  get filteredRows(): PermissionRow[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter(
      (r) =>
        r.usr_name.toLowerCase().includes(q) ||
        r.usr_mail.toLowerCase().includes(q),
    );
  }

  // Add this method:
  /**
   * Open the invite user dialog. On close, reload the grid if an invite
   * was successfully sent (in case the pending user appears in the list).
   */
  onInviteUser(): void {
    const ref = this.dialog.open(InviteUserComponent, {
      data: {
        orgName: this.orgName,
        permissions: this.permissions,
      },
      width: '500px',
      panelClass: 'custom-dialog-container',
    });

    ref.afterClosed().subscribe((inviteSent: boolean) => {
      if (inviteSent) {
        this.loadPermissionsThenData();
      }
    });
  }
  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }
}
